import { PlatformChartGateActivationDto } from '@data/_generated-api/platform-api/models/platform-chart-gate-activation-dto';
import { PlatformChartListItemDto } from '@data/_generated-api/platform-api/models/platform-chart-list-item-dto';
import {
  HD_PLANET_ORDER_STANDARD,
  HdPlanetKey,
  getHdPlanetDisplayConfigByPlanetId,
} from '../bodygraph/planet-grid/hd-planet-display-config';

/** Sortering van de poortzoektocht in de poort-info: op activatiegewicht of op naam. */
export type HdChartGateSearchSort = 'weight' | 'name';

/**
 * Gewicht van één activatie per planeet (spec 2026-10-30-poortzoektocht-
 * gewichtssortering). De score van een treffer is de SOM van haar activaties op
 * de gezochte poort, zodat aantal en zwaarte samen tellen. De getallen zijn zo
 * gekozen dat één Zon (12) boven twee Neptunus-activaties (4) en boven
 * Mercurius + Mars + Neptunus (10) staat, gelijk met Mercurius + Venus + Mars
 * (12, de tiebreak op planeetvolgorde zet de Zon dan eerst) en onder vier
 * persoonlijke planeten (16). Design en personality wegen gelijk. Chiron zit
 * niet in de doorzoekbare activaties en weegt niets.
 */
export const HD_GATE_ACTIVATION_WEIGHTS: Readonly<Record<HdPlanetKey, number>> = {
  sun: 12,
  earth: 10,
  northNode: 8,
  southNode: 8,
  moon: 6,
  mercury: 4,
  venus: 4,
  mars: 4,
  jupiter: 3,
  saturn: 3,
  uranus: 2,
  neptune: 2,
  pluto: 2,
  chiron: 0,
};

/** Gewicht van een activatie via haar planeet-id; onbekende planeet weegt niets. */
export function hdGateActivationWeight(planetId: number | null | undefined): number {
  if (planetId == null) return 0;
  const config = getHdPlanetDisplayConfigByPlanetId(planetId);
  return config ? HD_GATE_ACTIVATION_WEIGHTS[config.key] : 0;
}

/** Som van de gewichten van de activaties. */
export function hdGateMatchScore(activations: readonly PlatformChartGateActivationDto[]): number {
  return activations.reduce((sum, activation) => sum + hdGateActivationWeight(activation.planetId), 0);
}

/** Plaats van de planeet in de standaardvolgorde (zon = 0); onbekend achteraan. */
function planetRank(planetId: number | null | undefined): number {
  const config = planetId == null ? undefined : getHdPlanetDisplayConfigByPlanetId(planetId);
  const index = config ? HD_PLANET_ORDER_STANDARD.indexOf(config.key) : -1;
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

/** Zwaarste activatie eerst; bij gelijk gewicht de standaard planeetvolgorde, dan personality vóór design. */
function compareActivations(a: PlatformChartGateActivationDto, b: PlatformChartGateActivationDto): number {
  return hdGateActivationWeight(b.planetId) - hdGateActivationWeight(a.planetId)
    || planetRank(a.planetId) - planetRank(b.planetId)
    || Number(a.side === 'design') - Number(b.side === 'design');
}

export interface HdChartGateMatchSortable {
  readonly item: PlatformChartListItemDto;
  readonly activations: readonly PlatformChartGateActivationDto[];
}

/** Treffer met haar score; bij sortering op gewicht staan de activaties van zwaar naar licht. */
export type HdChartGateMatchScored<T extends HdChartGateMatchSortable> = T & { readonly score: number };

/**
 * Rangschikt de treffers van de poortzoektocht volgens de gekozen sortering
 * (client-side, zonder nieuwe zoektocht):
 * - `weight`: hoogste score eerst; bij gelijke score de treffer met de zwaarste
 *   planeet, dan op naam. De activaties per treffer staan dan ook van zwaar naar
 *   licht, zodat de chips de volgorde verklaren;
 * - `name`: alfabetisch, activaties zoals de zoektocht ze leverde.
 */
export function arrangeHdChartGateMatches<T extends HdChartGateMatchSortable>(
  matches: readonly T[],
  sort: HdChartGateSearchSort,
): HdChartGateMatchScored<T>[] {
  const byName = (a: HdChartGateMatchSortable, b: HdChartGateMatchSortable) =>
    (a.item.name ?? '').localeCompare(b.item.name ?? '');
  const scored = matches.map(match => ({ ...match, score: hdGateMatchScore(match.activations) }));
  if (sort === 'name') {
    return scored.sort(byName);
  }
  const bestRank = (match: HdChartGateMatchSortable) =>
    Math.min(Number.MAX_SAFE_INTEGER, ...match.activations.map(activation => planetRank(activation.planetId)));
  return scored
    .map(match => ({ ...match, activations: [...match.activations].sort(compareActivations) }))
    .sort((a, b) => b.score - a.score || bestRank(a) - bestRank(b) || byName(a, b));
}
